import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { DocumentGroup } from './grouping';
import {
  DeliverAs,
  DocumentHandling,
  GroupBy,
  GROUP_BY_LABELS,
  OrderBy,
  PackagingChoices,
  SINGLE_PDF_PAGE_LIMIT,
  estimatePages,
  fileExtensionFor,
} from './packaging';
import { optionToggleSx } from '../toggle-styles';
import { plural } from './format';

interface Props {
  open: boolean;
  onClose: () => void;
  choices: PackagingChoices;
  onChange: (choices: PackagingChoices) => void;
  groups: DocumentGroup[];
  selected: Set<string>;
  fileName: string;
  patientLabel: string;
  submitting: boolean;
  onRequest: () => void;
}

/**
 * SQ-168: the arrangement controls, and the confirm step, in one dialog.
 *
 * LJ's review: "the first thing that appears is the doc line items with an X, which can
 * make them think it's failing." So this shows the resulting tree instead — what the
 * worker will actually produce — and the toggles sit above it where their effect is
 * visible rather than described. One dialog at the end, not two (E3).
 */
export const ReviewAndRequestDialog: React.FC<Props> = ({
  open,
  onClose,
  choices,
  onChange,
  groups,
  selected,
  fileName,
  patientLabel,
  submitting,
  onRequest,
}) => {
  const selectedCount = selected.size;
  const isSinglePdf = choices.deliverAs === 'single_pdf';
  const estimatedPages = estimatePages(selectedCount);
  const willBeZipped = isSinglePdf && estimatedPages > SINGLE_PDF_PAGE_LIMIT;
  const extension = fileExtensionFor(choices, selectedCount);

  const set = <K extends keyof PackagingChoices>(key: K, value: PackagingChoices[K]) =>
    onChange({ ...choices, [key]: value });

  const selectedGroups = groups
    .map((g) => ({ ...g, documents: g.documents.filter((d) => selected.has(d.uuid)) }))
    .filter((g) => g.documents.length > 0);

  //  The worker groups by `group_by` — resident or therapist — never by document kind.
  //  Listing kinds as folders promised a tree it does not build. Grouped by resident this
  //  screen knows the answer exactly, because there is one resident; grouped by therapist
  //  it cannot, since the rows carry no therapist.
  const folderLines: string[] = (() => {
    const total = plural(selectedCount, 'document');
    if (choices.groupBy !== 'patient') {
      const per = GROUP_BY_LABELS[choices.groupBy].toLowerCase();
      return [
        isSinglePdf
          ? `one section per ${per} — ${total} in all`
          : `one folder per ${per} — ${total} in all`,
      ];
    }
    if (isSinglePdf) return [`§ ${patientLabel} — ${total}`];
    return choices.documentHandling === 'merge'
      ? [`${patientLabel}.pdf — ${total}, merged`]
      : [`${patientLabel}/ — ${total}`];
  })();

  //  SOAP merge runs unconditionally in the worker, so daily notes always arrive as one file.
  const dailyNotes = selectedGroups.find((g) => g.kind === 'DAILY_SOAP_NOTE');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Review &amp; request</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* Two shapes, and the choice changes what the rest of the dialog means — so it
              leads, and it is segmented rather than a dropdown that hides one of only two
              answers behind a click. Same idiom as the period basis on the page behind it. */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}
            >
              Deliver as
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={choices.deliverAs}
              onChange={(_e, next) => next && set('deliverAs', next as DeliverAs)}
              aria-label="Deliver as"
              sx={optionToggleSx}
            >
              <ToggleButton value="zip">A ZIP of files</ToggleButton>
              <ToggleButton value="single_pdf">One PDF</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* These two are independent of each other and each has few options, so they sit
              side by side rather than stacking two more full-width rows. */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
          >
            <TextField
              select
              fullWidth
              size="small"
              // Same value, same options, different meaning: folders in ZIP mode,
              // section order inside the file in PDF mode (E2c).
              label={isSinglePdf ? 'Group by' : 'One folder per'}
              value={choices.groupBy}
              onChange={(e) => set('groupBy', e.target.value as GroupBy)}
            >
              {(Object.keys(GROUP_BY_LABELS) as GroupBy[]).map((k) => (
                <MenuItem
                  key={k}
                  value={k}
                >
                  {GROUP_BY_LABELS[k]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Ordered by"
              value={choices.orderBy}
              onChange={(e) => set('orderBy', e.target.value as OrderBy)}
            >
              <MenuItem value="date_discipline">Date, oldest first</MenuItem>
              <MenuItem value="discipline_date">Discipline, then date</MenuItem>
            </TextField>
          </Stack>

          {/* Hidden, not disabled, in PDF mode — there are no folders for it to act on,
              so leaving it visible would pose a question with no meaning (E2a). A two-option
              dropdown for a yes/no was a control where a checkbox says it. */}
          {!isSinglePdf && (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0, mr: 1.25 }}
                  checked={choices.documentHandling === 'merge'}
                  onChange={(e) =>
                    set(
                      'documentHandling',
                      (e.target.checked ? 'merge' : 'subfolder') as DocumentHandling
                    )
                  }
                />
              }
              label={<Typography variant="body2">Combine each folder into a single PDF</Typography>}
            />
          )}

          {/* SQ-180: this one changes what each PDF looks like, not which documents the
              packet holds — which is why it sits here and the hold toggle sits with the
              documents. Phrased as "keep" so a tick means more content. */}
          <FormControlLabel
            sx={{ ml: 0, alignItems: 'flex-start' }}
            control={
              <Checkbox
                size="small"
                sx={{ p: 0, mr: 1.25, mt: 0.25 }}
                checked={choices.includeAuditLog}
                onChange={(e) => set('includeAuditLog', e.target.checked)}
                inputProps={{ 'aria-label': 'Keep the signature audit page' }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Keep the signature audit page</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Removed by default. The audit log is internal provenance, not part of the clinical
                  record. Signatures still print.
                </Typography>
              </Box>
            }
          />

          {willBeZipped && (
            <Alert severity="info">
              About {estimatedPages} pages. Over {SINGLE_PDF_PAGE_LIMIT} the PDF is delivered inside
              a ZIP so the download does not fail part-way — still one PDF, just compressed. You can
              go ahead.
            </Alert>
          )}

          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              What you will get
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: 13,
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              <div>
                {fileName}.{extension}
              </div>
              {folderLines.map((line) => (
                <div
                  key={line}
                  style={{ paddingLeft: 16 }}
                >
                  {line}
                </div>
              ))}
              {dailyNotes && !isSinglePdf && choices.documentHandling !== 'merge' && (
                <div style={{ paddingLeft: 32, opacity: 0.72 }}>
                  {plural(dailyNotes.documents.length, 'daily note')} combined into one PDF
                </div>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
        >
          Back
        </Button>
        <LoadingButton
          variant="contained"
          loading={submitting}
          disabled={selectedCount === 0}
          onClick={onRequest}
        >
          Request {plural(selectedCount, 'document')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
